import { buildCreatureAbilityCatalog } from './CreatureAbilityCatalog';
import { extractActionEconomy, flattenActionEconomy } from '../battleRoomActionUtils';
import { hydrateCharacterCombatState } from '../CharacterCombatReadiness';
import IntelligentContentParser from '../IntelligentContentParser';
import { normalizeAreaOfEffect } from '../areaOfEffectUtils';
import RangeCalculator from '../RangeCalculator';
import { buildStandardActionCatalogEntries } from '../../rules/core/standardActions';
import { collectCombatantSources } from './CombatantNormalizer';
import { isAmmunitionEntry } from '../EquipmentState';
import { enrichSpellActionData, normalizeSpellActionLookupKey } from './SpellActionReference';
import { buildCharacterItemActions } from '../CharacterRulesPresentation';
import {
  inferKnownWeaponStats,
  inferWeaponAttackType,
  inferWeaponMagicBonus,
  inferWeaponRange,
  inferWeaponReach,
  mergeWeaponBaseDamageEntries
} from './WeaponDamageInference';

const ACTION_FIELDS = ['actions', 'bonusActions', 'reactions'];
const GENERIC_PLACEHOLDER_NAMES = new Set(['attack', 'attack action', 'actions']);

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const asRecord = (value) => (
  value && typeof value === 'object' ? value : null
);

const emptyEconomy = () => ({
  actions: [],
  bonusActions: [],
  reactions: []
});

const normalizeActionName = (value) => String(value || '')
  .trim()
  .replace(/\s+/g, ' ');

const SPELL_SECTION_LABEL_PATTERNS = [
  /^cantrips?$/i,
  /^prepared spells?$/i,
  /^known spells?$/i,
  /^spells?$/i,
  /^rituals?$/i,
  /^at will$/i,
  /^\d+(st|nd|rd|th)\s+level\s+spells?$/i,
  /^level\s+\d+\s+spells?$/i
];

const humanizeSpellName = (value) => {
  const normalized = normalizeActionName(value);
  if (!normalized) return '';
  return normalized
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const shouldIgnoreSpellLabel = (value) => {
  const normalized = normalizeActionName(value);
  if (!normalized) return true;
  return SPELL_SECTION_LABEL_PATTERNS.some((pattern) => pattern.test(normalized));
};

const normalizeEntryNameKey = (entry) => normalizeActionName(
  entry?.name || entry?.title || entry?.label || ''
).toLowerCase();

const normalizeSpellDisplayName = (spell, enrichedSpellEntry) => {
  const rawName = normalizeActionName(
    enrichedSpellEntry?.name
    || enrichedSpellEntry?.id
    || (typeof spell === 'string' ? spell : (spell?.name || spell?.id))
  );
  if (shouldIgnoreSpellLabel(rawName)) return '';

  const lookupKey = normalizeSpellActionLookupKey(rawName);
  if (lookupKey && enrichedSpellEntry?.name) {
    const enrichedLookupKey = normalizeSpellActionLookupKey(enrichedSpellEntry.name);
    if (lookupKey === enrichedLookupKey) {
      return humanizeSpellName(enrichedSpellEntry.name);
    }
  }

  return humanizeSpellName(rawName);
};

const normalizeActivationType = (value, fallback = 'action') => {
  const normalized = String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

  if (normalized.includes('bonus')) return 'bonus action';
  if (normalized.includes('reaction')) return 'reaction';
  if (normalized.includes('free')) return 'free';
  return 'action';
};

const getEconomyFieldForActivation = (value) => {
  const normalized = normalizeActivationType(value);
  if (normalized === 'bonus action') return 'bonusActions';
  if (normalized === 'reaction') return 'reactions';
  return 'actions';
};

const buildRawEntryKey = (entry, fallbackField = 'actions') => {
  if (!entry || typeof entry !== 'object') return '';

  const name = normalizeActionName(entry.name || entry.title || entry.label).toLowerCase();
  const activation = normalizeActivationType(
    entry.activationType || entry.actionType || fallbackField
  );

  if (!name) {
    return '';
  }

  return `${activation}|${name}`;
};

const pushUniqueRawEntry = (bucket, field, entry, seenKeys) => {
  if (!entry || typeof entry !== 'object') return;

  const targetField = getEconomyFieldForActivation(
    entry.activationType || entry.actionType || field
  );
  const normalizedEntry = {
    ...entry,
    activationType: normalizeActivationType(
      entry.activationType || entry.actionType || targetField
    ),
    actionType: entry.actionType || normalizeActivationType(
      entry.activationType || entry.actionType || targetField
    )
  };

  const key = buildRawEntryKey(normalizedEntry, targetField);
  if (!key) return;
  if (seenKeys[targetField].has(key)) return;

  seenKeys[targetField].add(key);
  bucket[targetField].push(normalizedEntry);
};

const dedupeRawEconomy = (...economies) => {
  const bucket = emptyEconomy();
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  economies.forEach((economy) => {
    if (!economy || typeof economy !== 'object') return;
    ACTION_FIELDS.forEach((field) => {
      toArray(economy[field]).forEach((entry) => {
        pushUniqueRawEntry(bucket, field, entry, seenKeys);
      });
    });
  });

  return bucket;
};

const removeMisbucketedActionDuplicates = (economy) => {
  if (!economy || typeof economy !== 'object') {
    return emptyEconomy();
  }

  const protectedNames = new Set([
    ...toArray(economy.bonusActions).map(normalizeEntryNameKey),
    ...toArray(economy.reactions).map(normalizeEntryNameKey)
  ].filter(Boolean));

  if (protectedNames.size === 0) {
    return economy;
  }

  return {
    ...economy,
    actions: toArray(economy.actions).filter((entry) => {
      const entryNameKey = normalizeEntryNameKey(entry);
      if (!entryNameKey || !protectedNames.has(entryNameKey)) {
        return true;
      }

      const activationType = normalizeActivationType(
        entry?.activationType || entry?.actionType || 'action'
      );
      return activationType !== 'action';
    })
  };
};

const collectSpellEntries = (subject) => {
  const entries = [];
  const seen = new Set();

  const pushSpell = (spell, defaultLevel = null) => {
    if (!spell) return;

    const spellEntry = typeof spell === 'string'
      ? { name: spell, level: defaultLevel, type: 'spell' }
      : { ...spell, level: spell.level ?? defaultLevel, type: spell.type || 'spell' };
    const enrichedSpellEntry = enrichSpellActionData(spellEntry, { subject });

    const name = normalizeSpellDisplayName(spell, enrichedSpellEntry);
    if (!name) return;
    const key = normalizeSpellActionLookupKey(enrichedSpellEntry?.name || enrichedSpellEntry?.id) || name.toLowerCase();

    if (seen.has(key)) return;
    seen.add(key);

    entries.push({
      ...enrichedSpellEntry,
      name,
      type: 'spell'
    });
  };

  const pushSpellArray = (spellList, defaultLevel = null) => {
    toArray(spellList).forEach((spell) => pushSpell(spell, defaultLevel));
  };

  const pushSpellObjectMap = (spellMap, prepared = false) => {
    if (!spellMap || typeof spellMap !== 'object') return;
    Object.entries(spellMap).forEach(([level, spellList]) => {
      toArray(spellList).forEach((spell) => pushSpell(
        typeof spell === 'string'
          ? spell
          : { ...spell, ...(prepared ? { prepared: true } : {}) },
        Number.isNaN(Number(level)) ? null : Number(level)
      ));
    });
  };

  const collectSpellPool = (pool) => {
    if (!pool || typeof pool !== 'object') return;

    const normalizedSpellSource = { spells: { ...pool } };
    IntelligentContentParser.normalizeSpellcastingStructures(normalizedSpellSource);
    IntelligentContentParser.buildSpellEntriesFromSpellData(normalizedSpellSource.spells)
      .forEach((spell) => pushSpell(spell, spell?.level ?? null));

    [
      { field: 'spells', level: null },
      { field: 'entries', level: null },
      { field: 'known', level: null },
      { field: 'prepared', level: null },
      { field: 'atWill', level: null },
      { field: 'cantrips', level: 0 },
      { field: 'spellList', level: null },
      { field: 'preparedSpells', level: null },
      { field: 'knownSpells', level: null }
    ].forEach(({ field, level }) => pushSpellArray(pool[field], level));

    for (let level = 0; level <= 9; level += 1) {
      [`level${level}`, String(level)].forEach((key) => pushSpellArray(pool[key], level));
    }

    pushSpellObjectMap(pool.byLevel);
    pushSpellObjectMap(pool.known_spells);
    pushSpellObjectMap(pool.knownSpells);
    pushSpellObjectMap(pool.prepared_spells, true);
    pushSpellObjectMap(pool.preparedSpells, true);

    if (pool.alwaysPrepared && typeof pool.alwaysPrepared === 'object') {
      Object.values(pool.alwaysPrepared).forEach((spellList) => pushSpellArray(spellList));
    }
    if (pool.alwaysPreparedSpells && typeof pool.alwaysPreparedSpells === 'object') {
      Object.values(pool.alwaysPreparedSpells).forEach((spellList) => pushSpellArray(spellList));
    }
    if (pool.always_prepared_spells && typeof pool.always_prepared_spells === 'object') {
      Object.values(pool.always_prepared_spells).forEach((spellList) => pushSpellArray(spellList));
    }
  };

  collectCombatantSources(subject).forEach((source) => {
    [
      asRecord(source?.spells),
      asRecord(source?.spellcasting),
      asRecord(source?.spellbook),
      asRecord(source?.data?.spells),
      asRecord(source?.data?.spellcasting)
    ].filter(Boolean).forEach((pool) => {
      collectSpellPool(pool);
    });

    if (Array.isArray(source?.preparedSpells)) {
      pushSpellArray(source.preparedSpells);
    }
    if (Array.isArray(source?.spellList)) {
      pushSpellArray(source.spellList);
    }
  });

  return entries;
};

const collectWeaponEntries = (subject) => {
  const entries = collectCombatantSources(subject).flatMap((source) => ([
    ...toArray(source?.equipmentDetailed?.weapons),
    ...toArray(source?.equipment?.weapons),
    ...toArray(source?.weapons),
    ...toArray(source?.equippedWeapons),
    ...toArray(source?.inventory?.equippedWeapons),
    ...toArray(source?.inventory?.equipment?.weapons)
  ])).filter((entry) => !isAmmunitionEntry(entry));
  const seen = new Set();
  return entries.filter((entry) => {
    const key = normalizeActionName(entry?.name || entry?.label || entry?.title).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const parseNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const match = value.match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value && typeof value === 'object') {
    return parseNumber(
      value.value
      ?? value.base
      ?? value.toHit
      ?? value.attackBonus
      ?? value.reach
      ?? value.range
      ?? null
    );
  }

  return null;
};

const abilityModifierFromSubject = (subject, ability) => {
  const normalized = String(ability || '').trim().toLowerCase();
  const aliases = {
    strength: ['strength', 'str'],
    dexterity: ['dexterity', 'dex'],
    constitution: ['constitution', 'con'],
    intelligence: ['intelligence', 'int'],
    wisdom: ['wisdom', 'wis'],
    charisma: ['charisma', 'cha']
  };
  const keys = aliases[normalized] || [normalized];
  const sources = collectCombatantSources(subject);
  for (const source of sources) {
    const abilities = source?.abilities || source?.data?.abilities || source?.characterData?.abilities || source?.canonicalData?.abilities;
    for (const key of keys) {
      const score = Number(abilities?.[key]);
      if (Number.isFinite(score)) return Math.floor((score - 10) / 2);
    }
  }
  return 0;
};

const proficiencyBonusFromSubject = (subject) => {
  for (const source of collectCombatantSources(subject)) {
    const value = parseNumber(
      source?.proficiencyBonus
      ?? source?.combatStats?.proficiencyBonus
      ?? source?.stats?.proficiencyBonus
      ?? source?.characterData?.proficiencyBonus
      ?? source?.canonicalData?.proficiencyBonus
    );
    if (value != null) return value;
  }
  return 2;
};

const buildUniversalUnarmedStrike = (subject, monsterLike, context) => {
  const strengthModifier = abilityModifierFromSubject(subject, 'strength');
  const attackBonus = strengthModifier + proficiencyBonusFromSubject(subject);
  return buildFormattedAction({
    name: 'Unarmed Strike',
    type: 'weapon',
    activationType: 'action',
    actionType: 'action',
    description: 'Make a melee attack using Strength. On a hit, deal at least 1 bludgeoning damage.',
    attackBonus,
    damage: [{ dice: '1d1', type: 'bludgeoning' }],
    range: '5 feet',
    weaponData: {
      name: 'Unarmed Strike',
      type: 'weapon',
      attackType: 'meleeWeapon',
      natural: true
    },
    attack: {
      type: 'meleeWeapon',
      toHit: attackBonus,
      reach: 5,
      damage: [{ dice: '1d1', type: 'bludgeoning' }]
    }
  }, monsterLike, context);
};

const normalizeDamageEntry = (entry) => {
  if (!entry) return null;

  if (typeof entry === 'string') {
    return { dice: entry, type: '' };
  }

  if (typeof entry !== 'object') return null;

  const dice = entry.dice || entry.formula || entry.damage || entry.damageFormula || '';
  const type = entry.type || entry.damageType || '';
  if (!dice && !type) return null;

  return { dice, type };
};

const parseDamageEntriesFromText = (text) => {
  if (typeof text !== 'string' || !text.trim()) return [];

  const matches = Array.from(text.matchAll(/(\d+d\d+(?:\s*[+-]\s*\d+)?)\s+([a-z]+)\s+damage/gi));
  return matches.map((match) => ({
    dice: String(match[1] || '').replace(/\s+/g, ''),
    type: String(match[2] || '').toLowerCase()
  }));
};

const parseAttackBonusFromText = (text) => {
  if (typeof text !== 'string') return null;
  const match = text.match(/([+-]?\d+)\s+to\s+hit/i);
  return match ? parseNumber(match[1]) : null;
};

const parseRangeFromText = (text) => {
  if (typeof text !== 'string') return null;

  const pairedMatch = text.match(/range\s+(\d+)\s*\/\s*(\d+)\s*(?:foot|feet|ft)/i);
  if (pairedMatch) {
    return {
      normal: Number(pairedMatch[1]),
      long: Number(pairedMatch[2])
    };
  }

  const reachMatch = text.match(/reach\s+(\d+)\s*(?:foot|feet|ft)/i);
  if (reachMatch) {
    return `${reachMatch[1]} feet`;
  }

  const rangeMatch = text.match(/range\s+(\d+)\s*(?:foot|feet|ft)/i);
  if (rangeMatch) {
    return `${rangeMatch[1]} feet`;
  }

  return null;
};

const parseRangeFromProperties = (properties) => {
  const combinedProperties = toArray(properties)
    .map((property) => String(property || '').trim())
    .filter(Boolean)
    .join(' | ');

  if (!combinedProperties) {
    return null;
  }

  const pairedMatch = combinedProperties.match(/(\d+)\s*\/\s*(\d+)/);
  if (pairedMatch) {
    return {
      normal: Number(pairedMatch[1]),
      long: Number(pairedMatch[2])
    };
  }

  const singleMatch = combinedProperties.match(/(\d+)\s*(?:foot|feet|ft)/i);
  if (singleMatch) {
    return `${singleMatch[1]} feet`;
  }

  return null;
};

const inferAttackKind = (entry, text = '') => {
  const explicitType = String(entry?.attackType || entry?.attack?.type || entry?.kind || '')
    .trim()
    .toLowerCase();

  if (explicitType.includes('ranged')) return 'ranged';
  if (explicitType.includes('melee')) return 'melee';

  const lowerText = String(text || '').toLowerCase();
  if (lowerText.includes('ranged weapon attack') || lowerText.includes('ranged spell attack')) {
    return 'ranged';
  }
  if (lowerText.includes('melee weapon attack') || lowerText.includes('melee spell attack')) {
    return 'melee';
  }

  const normalizedRange = normalizeRangeValue(entry?.range || entry?.attack?.range || entry?.reach || null);
  if (normalizedRange && typeof normalizedRange === 'object') return 'ranged';

  return null;
};

const normalizeRangeValue = (value) => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (lower === 'self' || lower === 'touch') return trimmed;

    const pairedMatch = trimmed.match(/(\d+)\s*\/\s*(\d+)/);
    if (pairedMatch) {
      return {
        normal: Number(pairedMatch[1]),
        long: Number(pairedMatch[2])
      };
    }

    const parsed = RangeCalculator.parseRangeValue(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? `${parsed} feet` : (Number.isFinite(parsed) ? null : trimmed);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? `${value} feet` : null;
  }

  if (typeof value === 'object') {
    const resolved = RangeCalculator.resolveRangeBands(value);
    if (Number.isFinite(resolved.normalRange) && Number.isFinite(resolved.longRange)) {
      return {
        normal: resolved.normalRange,
        long: resolved.longRange
      };
    }
    if (Number.isFinite(resolved.maxRange)) {
      return `${resolved.maxRange} feet`;
    }
  }

  return null;
};

const buildAttackPayload = (entry, context = {}) => {
  const description = entry.description || entry.desc || entry.text || '';
  const originalAttack = asRecord(entry.attack);
  const kind = inferAttackKind(entry, description);
  const attackType = originalAttack?.type
    || (kind === 'ranged' ? 'rangedWeapon' : (kind === 'melee' ? 'meleeWeapon' : null));

  const inferredRange = inferWeaponRange(entry);
  const normalizedRange = normalizeRangeValue(
    entry.range
    || entry.attackRange
    || originalAttack?.range
    || originalAttack?.reach
    || parseRangeFromText(description)
    || inferredRange
  );

  const damageEntries = toArray(entry.damage).map(normalizeDamageEntry).filter(Boolean);
  const legacyDamageEntry = normalizeDamageEntry(
    typeof entry.damage === 'string' && entry.damage.trim()
      ? {
          dice: entry.damage.trim(),
          type: entry.damageType || entry.type || ''
        }
      : null
  );
  const attackDamageEntries = toArray(originalAttack?.damage).map(normalizeDamageEntry).filter(Boolean);
  const parsedDamageEntries = damageEntries.length > 0
    ? damageEntries
    : (legacyDamageEntry
      ? [legacyDamageEntry]
      : (attackDamageEntries.length > 0
        ? attackDamageEntries
        : parseDamageEntriesFromText(description)));
  const effectiveDamageEntries = mergeWeaponBaseDamageEntries(entry, parsedDamageEntries, context);
  const inferredAttackType = inferWeaponAttackType(entry);
  const weaponMagicBonus = inferWeaponMagicBonus(entry);

  const toHit = parseNumber(
    entry.attackBonus
    ?? entry.bonus
    ?? entry.hitBonus
    ?? entry.toHit
    ?? originalAttack?.toHit
    ?? parseAttackBonusFromText(description)
  );

  const reach = parseNumber(
    entry.reach
    ?? originalAttack?.reach
    ?? inferWeaponReach(entry)
    ?? (typeof normalizedRange === 'string' && normalizedRange.toLowerCase().includes('feet') ? normalizedRange : null)
  );

  const attack = {
    ...(originalAttack || {})
  };

  if (attackType || inferredAttackType) {
    attack.type = attackType || inferredAttackType;
  }
  if (toHit != null) {
    attack.toHit = toHit;
  }
  if (kind === 'melee' && reach != null) {
    attack.reach = reach;
  }
  if (kind === 'ranged' && normalizedRange) {
    attack.range = normalizedRange;
  }
  if (!attack.range && inferredAttackType === 'rangedWeapon' && normalizedRange) {
    attack.range = normalizedRange;
  }
  if (!attack.reach && inferredAttackType === 'meleeWeapon' && reach != null) {
    attack.reach = reach;
  }
  if (effectiveDamageEntries.length > 0) {
    attack.damage = effectiveDamageEntries;
  }
  if (weaponMagicBonus) {
    attack.magicBonus = weaponMagicBonus;
  }
  const hasMechanicalAttackPayload = Object.keys(attack).length > 0;
  if (description && hasMechanicalAttackPayload && !attack.notes) {
    attack.notes = description;
  }

  return hasMechanicalAttackPayload ? attack : null;
};

const inferActionType = (entry, attackPayload) => {
  const explicitType = String(entry.type || '').trim().toLowerCase();
  if (explicitType === 'spell') return 'spell';
  if (explicitType === 'weapon') return 'weapon';
  if (explicitType === 'legendary') return 'legendary';
  if (explicitType === 'reaction') return 'reaction';
  if (explicitType === 'bonus') return 'bonus';
  if (explicitType === 'move') return 'move';
  if (explicitType === 'free') return 'free';
  if (explicitType === 'object') return 'object';
  if (explicitType === 'attack') return 'attack';

  if (entry.spellLevel != null || entry.level != null || entry.school) {
    return 'spell';
  }

  if (attackPayload) {
    return entry.weaponData ? 'weapon' : 'attack';
  }

  return explicitType || 'action';
};

const EXECUTION_ROUTES = new Set(['attack', 'spell', 'ability', 'standard']);
const TARGET_REQUIREMENTS = new Set(['none', 'self', 'creature', 'object', 'point', 'area', 'trigger']);

/**
 * Compile catalog source data into the explicit execution contract consumed by
 * BattleRoom. Runtime request handling never repeats this classification.
 * rule_reference: AGENTS.md explicit routing and no mechanics inferred from names.
 * date_of_change: 2026-08-21
 */
const resolveExecutionRoute = (entry, actionType, attackPayload) => {
  if (entry.executionRoute != null) {
    if (!EXECUTION_ROUTES.has(entry.executionRoute)) {
      throw new Error(`action.executionRoute must be attack, spell, ability, or standard; received ${String(entry.executionRoute)}.`);
    }
    return entry.executionRoute;
  }
  if (entry.standardActionKey) return 'standard';
  if (actionType === 'spell') return 'spell';
  if (attackPayload) return 'attack';
  return 'ability';
};

const resolveTargetRequirement = (entry, executionRoute, normalizedRange, areaOfEffect) => {
  const explicit = entry.targetRequirement || entry.targetingType || entry.mechanics?.targeting;
  if (explicit != null) {
    const normalized = String(explicit).trim().toLowerCase();
    if (!TARGET_REQUIREMENTS.has(normalized)) {
      throw new Error(`action.targetRequirement must be none, self, creature, object, point, area, or trigger; received ${String(explicit)}.`);
    }
    return normalized;
  }
  if (areaOfEffect) return 'area';
  if (String(normalizedRange || '').trim().toLowerCase() === 'self') return 'self';
  if (executionRoute === 'attack') return 'creature';
  if (entry.save || entry.savingThrow || entry.saveType || entry.healing || entry.damage || entry.requiresAttackRoll) {
    return 'creature';
  }
  return 'none';
};

const shouldHideCatalogEntry = (entry) => {
  const normalizedName = normalizeActionName(entry?.name || entry?.title || '').toLowerCase();
  if (!normalizedName) return true;

  // date_of_change: 2026-09-06
  // rule_reference: Existing imports may contain a flattened character sheet in an action slot;
  // never expose that metadata blob as a readable or executable combat option.
  if (IntelligentContentParser.isCharacterSheetMetadataDump(entry)) {
    return true;
  }

  if (GENERIC_PLACEHOLDER_NAMES.has(normalizedName) && !entry?.attack && !entry?.originalAction?.attack) {
    return true;
  }

  return false;
};

const buildFormattedAction = (entry, monsterLike = false, context = {}) => {
  if (!entry || typeof entry !== 'object') return null;

  const initialAttackPayload = buildAttackPayload(entry, context);
  const initialActionType = inferActionType(entry, initialAttackPayload);
  if (initialActionType === 'spell' && !entry.acquisition && !entry.modification) {
    entry = enrichSpellActionData(entry);
  }

  const attackPayload = buildAttackPayload(entry, context);
  const actionType = inferActionType(entry, attackPayload);
  const normalizedRange = normalizeRangeValue(
    entry.range
    || entry.attackRange
    || attackPayload?.range
    || attackPayload?.reach
    || parseRangeFromText(entry.description || entry.desc || entry.text || '')
  );

  const normalizedDamage = toArray(entry.damage).map(normalizeDamageEntry).filter(Boolean);
  const fallbackDamage = attackPayload?.damage || [];
  const damage = fallbackDamage.length >= normalizedDamage.length
    ? fallbackDamage
    : normalizedDamage;
  const parsedAttackBonus = parseNumber(entry.attackBonus ?? entry.bonus ?? entry.hitBonus ?? entry.toHit ?? attackPayload?.toHit);
  const weaponMagicBonus = inferWeaponMagicBonus(entry);

  const normalizedArea = normalizeAreaOfEffect(entry.areaOfEffect || null);
  const areaType = normalizedArea?.shape || null;
  const areaSize = normalizedArea?.size || null;
  const executionRoute = resolveExecutionRoute(entry, actionType, attackPayload);
  const targetRequirement = resolveTargetRequirement(
    entry,
    executionRoute,
    normalizedRange,
    normalizedArea
  );

  const formatted = {
    ...entry,
    type: actionType,
    name: normalizeActionName(entry.name || entry.title || entry.label || 'Unknown Action'),
    desc: entry.desc || entry.description || entry.text || attackPayload?.notes || '',
    damage: damage.length > 0
      ? damage
      : ((typeof entry.damage === 'string' && entry.damage.trim())
        ? entry.damage.trim()
        : (entry.damageFormula || entry.damageRoll || '')),
    range: normalizedRange,
    actionType: entry.actionType || normalizeActivationType(entry.activationType || entry.actionType),
    activationType: normalizeActivationType(entry.activationType || entry.actionType),
    executionRoute,
    targetRequirement,
    areaOfEffect: normalizedArea,
    areaType,
    areaSize,
    originalAction: entry.originalAction || entry
  };

  if (parsedAttackBonus != null) {
    formatted.bonus = parsedAttackBonus;
  }
  if (weaponMagicBonus) {
    formatted.magicBonus = weaponMagicBonus;
  }

  if (attackPayload) {
    formatted.attack = attackPayload;
  }

  if (monsterLike) {
    formatted.isMonsterAction = true;
  }

  return formatted;
};

const buildCatalogKey = (entry) => {
  const name = normalizeActionName(entry?.name || '').toLowerCase();
  const activation = normalizeActivationType(entry?.activationType || entry?.actionType || 'action');
  const type = String(entry?.type || '').trim().toLowerCase();
  return name ? `${activation}|${type}|${name}` : '';
};

const dedupeCatalogEntries = (entries) => {
  const seen = new Set();
  return toArray(entries).filter((entry) => {
    const key = buildCatalogKey(entry);
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildLegacyCharacterActionEconomy = (subject) => {
  const economy = emptyEconomy();
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  collectWeaponEntries(subject).forEach((weapon) => {
    const typeLabel = String(weapon?.type || '').trim().toLowerCase();
    const loweredName = normalizeActionName(weapon?.name || weapon?.label).toLowerCase();
    const hasAttackStats = Boolean(
      weapon?.damage
      || weapon?.damageFormula
      || weapon?.attackBonus != null
      || weapon?.attack
    );
    if ((typeLabel === 'shield' || loweredName === 'shield') && !hasAttackStats) {
      return;
    }

    const name = normalizeActionName(weapon?.name || weapon?.label || 'Weapon Attack');
    if (!name) return;

    const normalizedRange = normalizeRangeValue(
      weapon.range
      || weapon.attackRange
      || weapon.rangeFeet
      || parseRangeFromProperties(weapon.properties)
      || parseRangeFromText(weapon.statLine || weapon.description || '')
    );

    const inferredWeaponStats = inferKnownWeaponStats(weapon);
    const properties = [
      ...toArray(inferredWeaponStats?.properties).map((property) => String(property)),
      ...toArray(weapon.properties).map((property) => String(property))
    ];
    const isRanged = Boolean(
      properties.some((property) => /ranged|ammunition|thrown/i.test(property))
      || (normalizedRange && typeof normalizedRange === 'object')
      || /bow|crossbow|dart|javelin|sling/i.test(loweredName)
    );

    const damageFormula = weapon.damageFormula || weapon.damage || weapon.statLine || '';
    const damageEntries = toArray(weapon.damage).map(normalizeDamageEntry).filter(Boolean);
    const parsedDamageEntries = damageEntries.length > 0
      ? damageEntries
      : parseDamageEntriesFromText(`${damageFormula} ${weapon.description || ''}`);
    const effectiveDamageEntries = mergeWeaponBaseDamageEntries(weapon, parsedDamageEntries, {
      abilityModifierForAbility: (ability) => abilityModifierFromSubject(subject, ability)
    });
    const weaponAttackBonus = parseNumber(weapon.attackBonus);
    const weaponMagicBonus = inferWeaponMagicBonus(weapon);
    const inferredRange = inferWeaponRange(weapon);
    const inferredReach = inferWeaponReach(weapon);
    const inferredAttackType = inferWeaponAttackType(weapon);

    pushUniqueRawEntry(
      economy,
      'actions',
      {
        name,
        type: 'weapon',
        activationType: 'action',
        actionType: 'action',
        description: weapon.description || weapon.statLine || `Attack with ${name}`,
        ...(weaponAttackBonus != null ? { attackBonus: weaponAttackBonus } : {}),
        ...(weaponMagicBonus ? { magicBonus: weaponMagicBonus } : {}),
        damage: effectiveDamageEntries,
        range: normalizedRange || inferredRange || (isRanged ? { normal: 80, long: 320 } : '5 feet'),
        properties,
        weaponData: weapon,
        attack: {
          type: inferredAttackType || (isRanged ? 'rangedWeapon' : 'meleeWeapon'),
          ...(weaponAttackBonus != null ? { toHit: weaponAttackBonus } : {}),
          ...(isRanged
            ? { range: normalizedRange || inferredRange || { normal: 80, long: 320 } }
            : { reach: parseNumber(weapon.reach) || inferredReach || 5 }),
          ...(weaponMagicBonus ? { magicBonus: weaponMagicBonus } : {}),
          damage: effectiveDamageEntries
        }
      },
      seenKeys
    );
  });

  collectSpellEntries(subject).forEach((spell) => {
    const castingTime = String(
      spell.castingTime
      || spell.castTime
      || spell.activationType
      || spell.actionType
      || 'action'
    ).toLowerCase();
    const activationType = castingTime.includes('bonus')
      ? 'bonus action'
      : (castingTime.includes('reaction') ? 'reaction' : 'action');
    const field = getEconomyFieldForActivation(activationType);

    pushUniqueRawEntry(
      economy,
      field,
      {
        ...spell,
        name: normalizeActionName(spell.name || spell.id),
        type: 'spell',
        activationType,
        actionType: activationType,
        description: spell.description || spell.desc || '',
        range: normalizeRangeValue(spell.range || spell.rangeFeet || parseRangeFromText(spell.description || '')),
        spellLevel: spell.level ?? spell.spellLevel ?? null
      },
      seenKeys
    );
  });

  buildCharacterItemActions(subject).forEach((itemAction) => {
    pushUniqueRawEntry(
      economy,
      getEconomyFieldForActivation(itemAction.activationType),
      itemAction,
      seenKeys
    );
  });

  return economy;
};

const isMonsterLike = (subject) => String(
  subject?.type
  || subject?.originalType
  || subject?.monsterData?.type
  || subject?.characterData?.type
  || ''
).toLowerCase() === 'monster';

const collectActionSources = (subject) => collectCombatantSources(subject);

const buildRecordedSpellLevelMap = (subject) => {
  const spellLevels = new Map();
  const record = (entry, level) => {
    const name = typeof entry === 'string' ? entry : (entry?.name || entry?.id);
    const key = normalizeSpellActionLookupKey(name);
    const numericLevel = Number(typeof entry === 'object' && entry?.level != null ? entry.level : level);
    if (!key || !Number.isFinite(numericLevel) || spellLevels.has(key)) return;
    spellLevels.set(key, {
      level: numericLevel
    });
  };

  collectCombatantSources(subject).forEach((source) => {
    const pools = [
      source?.spells,
      source?.spellcasting,
      source?.data?.spells,
      source?.data?.spellcasting
    ].filter((pool) => pool && typeof pool === 'object');
    pools.forEach((pool) => {
      if (pool.byLevel && typeof pool.byLevel === 'object') {
        Object.entries(pool.byLevel).forEach(([level, entries]) => {
          toArray(entries).forEach((entry) => record(entry, level));
        });
      }
      toArray(pool.cantrips).forEach((entry) => record(entry, 0));
      for (let level = 0; level <= 9; level += 1) {
        toArray(pool[`level${level}`]).forEach((entry) => record(entry, level));
        toArray(pool[String(level)]).forEach((entry) => record(entry, level));
      }
    });
  });

  return spellLevels;
};

export const buildCombatActionEconomy = (subject) => {
  if (!subject || typeof subject !== 'object') {
    return emptyEconomy();
  }

  const structuredEconomy = dedupeRawEconomy(
    extractActionEconomy(...collectActionSources(subject))
  );

  if (isMonsterLike(subject)) {
    return structuredEconomy;
  }

  const characterSource = hydrateCharacterCombatState(subject);
  const readinessEconomy = dedupeRawEconomy(
    extractActionEconomy(characterSource)
  );
  const legacyEconomy = buildLegacyCharacterActionEconomy(characterSource);

  return removeMisbucketedActionDuplicates(
    dedupeRawEconomy(structuredEconomy, readinessEconomy, legacyEconomy)
  );
};

export const buildCombatActionCatalog = (subject) => {
  const creatureCatalog = buildCreatureAbilityCatalog(subject);
  if (creatureCatalog) return creatureCatalog;
  if (!subject || typeof subject !== 'object') {
    return [];
  }

  const monsterLike = isMonsterLike(subject);
  const context = {
    abilityModifierForAbility: (ability) => abilityModifierFromSubject(subject, ability)
  };
  const economy = buildCombatActionEconomy(subject);
  const flattened = flattenActionEconomy(economy);
  const canonicalSpellMetadata = monsterLike ? new Map() : buildRecordedSpellLevelMap(subject);
  const formattedEntries = flattened
    .map((entry) => {
      const isSpellEntry = String(entry?.type || '').trim().toLowerCase() === 'spell';
      const metadata = isSpellEntry
        ? canonicalSpellMetadata.get(normalizeSpellActionLookupKey(entry?.name || entry?.id))
        : null;
      const hasRecordedDamage = Array.isArray(entry?.damage)
        ? entry.damage.length > 0
        : Boolean(String(entry?.damage || '').trim());
      const needsMetadata = metadata && (
        entry.level == null
        && metadata.level != null
        && entry.utilitySpell !== true
        && !entry.modification
        && Boolean(
          hasRecordedDamage
          || entry.healing
          || entry.save
          || entry.savingThrow
          || entry.saveType
          || entry.attack
        )
      );
      const enrichedEntry = needsMetadata
        ? {
            ...entry,
            level: metadata.level
          }
        : entry;
      return buildFormattedAction(enrichedEntry, monsterLike, context);
    })
    .filter(Boolean)
    .filter((entry) => !shouldHideCatalogEntry(entry));

  const legendaryActions = monsterLike
    ? toArray(
      subject?.legendaryActions
      || subject?.monsterData?.legendaryActions
      || subject?.monsterData?.data?.legendaryActions
    ).map((entry) => buildFormattedAction({
      ...entry,
      type: 'legendary',
      activationType: entry.activationType || entry.actionType || 'action',
      actionType: entry.actionType || entry.activationType || 'action'
    }, true, context)).filter(Boolean)
    : [];
  const universalUnarmedStrike = buildUniversalUnarmedStrike(subject, monsterLike, context);
  const catalog = dedupeCatalogEntries([...formattedEntries, ...legendaryActions]);
  const unarmedIndex = catalog.findIndex((entry) => normalizeActionName(entry?.name).toLowerCase() === 'unarmed strike');
  if (unarmedIndex < 0) return dedupeCatalogEntries([...catalog, universalUnarmedStrike]);

  const existing = catalog[unarmedIndex];
  const existingDamage = toArray(existing?.damage);
  if (existingDamage.length > 0 && (existing?.attack || existing?.bonus != null || existing?.attackBonus != null)) return catalog;
  catalog[unarmedIndex] = {
    ...universalUnarmedStrike,
    ...existing,
    bonus: existing?.bonus ?? universalUnarmedStrike?.bonus,
    damage: existingDamage.length > 0 ? existing.damage : universalUnarmedStrike.damage,
    attack: {
      ...(universalUnarmedStrike?.attack ?? {}),
      ...(existing?.attack ?? {}),
      damage: toArray(existing?.attack?.damage).length > 0
        ? existing.attack.damage
        : universalUnarmedStrike?.attack?.damage,
    },
  };
  return catalog;
};

export const buildStandardCombatActions = (speed) => {
  // Character/class bonus actions must originate in the canonical sheet or a
  // structured equipped-weapon derivation. Generic labels here advertised
  // mechanics that the engine could not resolve.
  // rule_reference: AGENTS.md - no hardcoded character mechanics or fake actions.
  // date_of_change: 2026-07-18
  return buildStandardActionCatalogEntries(speed);
};

export default buildCombatActionCatalog;
